// Fixed positioning for Wall3DBox.jsx

import React from 'react';

/**
 * Get material roughness based on wall material type
 */
const getMaterialRoughness = (material) => {
  const roughnessMap = {
    drywall: 0.8,
    brick: 0.9,
    concrete: 0.9,
    wood: 0.7,
    metal: 0.2,
    glass: 0.1,
    fabric: 0.9,
    stone: 0.8
  };
  return roughnessMap[material] || 0.8; // Default to drywall
};

/**
 * Get material metalness based on wall material type
 */
const getMaterialMetalness = (material) => {
  const metalnessMap = {
    drywall: 0.0,
    brick: 0.0,
    concrete: 0.0,
    wood: 0.0,
    metal: 0.8,
    glass: 0.1,
    fabric: 0.0,
    stone: 0.0
  };
  return metalnessMap[material] || 0.0; // Default to non-metallic
};

/**
 * Get texture URL for material (using simpler canvas-generated data URLs)
 */
const getMaterialTexture = (material) => {
  // For now, let's try a simpler approach with canvas-generated textures
  const generateBrickTexture = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    
    // Fill background with brick red
    ctx.fillStyle = '#B22222';
    ctx.fillRect(0, 0, 128, 64);
    
    // Draw brick pattern
    ctx.fillStyle = '#8B0000';
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1;
    
    // First row of bricks
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(i * 42, 0, 40, 20);
      ctx.strokeRect(i * 42, 0, 40, 20);
    }
    
    // Second row (offset)
    for (let i = 0; i < 2; i++) {
      ctx.fillRect(i * 42 + 21, 22, 40, 20);
      ctx.strokeRect(i * 42 + 21, 22, 40, 20);
    }
    
    // Third row
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(i * 42, 44, 40, 20);
      ctx.strokeRect(i * 42, 44, 40, 20);
    }
    
    return canvas.toDataURL();
  };
  
  const generateConcreteTexture = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    
    // Fill with concrete gray
    ctx.fillStyle = '#C0C0C0';
    ctx.fillRect(0, 0, 128, 128);
    
    // Add noise
    const imageData = ctx.getImageData(0, 0, 128, 128);
    const data = imageData.data;
    
    for (let i = 0; i < data.length; i += 4) {
      const noise = Math.random() * 40 - 20;
      data[i] = Math.max(0, Math.min(255, data[i] + noise));     // Red
      data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise)); // Green
      data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise)); // Blue
    }
    
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
  };
  
  const textureMap = {
    brick: generateBrickTexture(),
    concrete: generateConcreteTexture()
  };
  
  return textureMap[material] || null;
};

/**
 * FIXED: Better artwork positioning calculation
 * The issue is in how we convert wall coordinates to 3D positions
 */
export const Wall3DBox = ({ 
  wall, 
  artworks = [], 
  selectedElement, 
  onSelectElement,
  onWallMoved,
  isMovable = true,
  getTextureUrl = (url) => url
}) => {
  // Handle wall movement
  const handleWallMoved = (event) => {
    if (!isMovable || !onWallMoved) return;
    
    const wallEl = event.target;
    const position = wallEl.getAttribute('position');
    const rotation = wallEl.getAttribute('rotation');
    
    // Convert feet back to inches for database
    const newPosition = {
      x: position.x * 12,
      y: position.y * 12,
      z: position.z * 12
    };
    
    const newRotation = {
      y: rotation.y
    };
    
    console.log(`🏛️ Wall ${wall.element_id} moved to:`, newPosition, newRotation);
    onWallMoved(wall.element_id, newPosition, newRotation);
  };
  const wallWidthFeet = wall.width / 12;
  const wallHeightFeet = wall.height / 12;
  const wallDepthFeet = 2 / 12; // 2 inches = 0.167 feet
  
  // Parse metadata — all pod walls are tent panels
  let wallMeta = {};
  try { wallMeta = wall.text_content ? JSON.parse(wall.text_content) : {}; } catch { /* ignore */ }
  const isTentPanel = !!wallMeta.pod_group; // any wall in a pod group is a tent panel
  const tiltAngle = isTentPanel ? (wallMeta.tilt_angle || 70) : 0;

  const centerX = (wall.wall_center_x || 0) / 12;
  const centerZ = (wall.wall_center_z || 0) / 12;
  const rotationY = wall.wall_rotation_y || 0;

  // For tent panels, the base edge sits on the floor and the panel tilts.
  // wall_center_y from the DB stores the vertical center; for tent panels we
  // recalculate so the base rests on y=0.
  let centerY;
  let rotationX = 0;
  if (isTentPanel) {
    const tiltRad = (tiltAngle * Math.PI) / 180;
    // Center of the tilted panel: half-height along the sin component
    centerY = (wallHeightFeet * Math.sin(tiltRad)) / 2;
    // A-Frame rotation: positive X tilts forward (top away from viewer).
    // We want the base on the ground and the top leaning, so rotate by -(90 - tilt).
    rotationX = -(90 - tiltAngle);
  } else {
    centerY = (wall.wall_center_y || wall.height / 2) / 12;
  }
  
  // Debug wall information
  console.log(`🏛️ Rendering wall ${wall.element_id}:`, {
    source: wall.source,
    dimensions: `${wall.width}" × ${wall.height}"`,
    center3D: `(${centerX.toFixed(1)}, ${centerY.toFixed(1)}, ${centerZ.toFixed(1)})`,
    artworkCount: artworks.length
  });
  
  const wallClass = `wall-3d-box ${wall.needsPositioning ? 'needs-positioning' : ''} ${wall.isStaged ? 'staged' : 'positioned'}`;
  
  return (
    <a-entity className="wall-container" data-wall-id={wall.element_id}>
      {/* Main wall box (2" deep) - Make movable if enabled */}
      <a-box
        id={`wall-${wall.element_id}`}
        width={wallWidthFeet}
        height={wallHeightFeet}
        depth={wallDepthFeet}
        position={`${centerX} ${centerY} ${centerZ}`}
        rotation={`${rotationX} ${rotationY} 0`}
        material={(() => {
          const materialTexture = getMaterialTexture(wall.color);
          const baseColor = wall.needsPositioning ? '#ffeb3b' : (wall.background_color || '#f5f5f5');
          const roughness = getMaterialRoughness(wall.color);
          const metalness = getMaterialMetalness(wall.color);
          
          console.log('🎨 Wall3DBox Material Debug:', {
            wallId: wall.element_id,
            materialType: wall.color,
            backgroundColor: wall.background_color,
            hasTexture: !!materialTexture,
            textureUrl: materialTexture ? materialTexture.substring(0, 50) + '...' : 'none',
            needsPositioning: wall.needsPositioning
          });
          
          // Read opacity from text_content JSON
          let wallOpacity = 1;
          try { const wMeta = wall.text_content ? JSON.parse(wall.text_content) : {}; wallOpacity = wMeta.opacity ?? 1; } catch { /* ignore */ }
          const opacitySuffix = wallOpacity < 1 ? `; opacity: ${wallOpacity}; transparent: true` : '';

          if (materialTexture && !wall.needsPositioning) {
            const materialString = `src: ${materialTexture}; roughness: ${roughness}; metalness: ${metalness}; repeat: 4 4${opacitySuffix}`;
            console.log('🎨 Using texture material:', materialString.substring(0, 100) + '...');
            return materialString;
          } else {
            const materialString = `color: ${baseColor}; roughness: ${roughness}; metalness: ${metalness}${opacitySuffix}`;
            console.log('🎨 Using color material:', materialString);
            return materialString;
          }
        })()}
        className={wallClass}
        data-wall-id={wall.element_id}
        data-movable={isMovable}
        onClick={() => onSelectElement && onSelectElement(wall)}
        {...(isMovable && {
          draggable: true,
          'drag-listeners': true,
          'drag-target': `#wall-${wall.element_id}`,
          'drag-constraint': 'lockY: false; lockZ: false',
          'drag-dropzone': '.wall-container',
          'drag-end': handleWallMoved
        })}
      >
        {/* Wall label */}
        <a-text
          value={`${wall.source || 'Wall ' + wall.element_id} (${artworks.length} artworks)`}
          position={`0 ${wallHeightFeet/2 + 0.3} 0.1`}
          align="center"
          color={wall.needsPositioning ? '#ff5722' : '#666'}
          width="6"
          className="wall-label"
        />
        
        {/* Movement indicator for movable walls - only when selected */}
        {isMovable && selectedElement && selectedElement.element_id === wall.element_id && (
          <a-ring
            radius-inner={Math.min(wallWidthFeet, wallHeightFeet) * 0.3}
            radius-outer={Math.min(wallWidthFeet, wallHeightFeet) * 0.35}
            position={`0 0 ${wallDepthFeet/2 + 0.08}`}
            material="color: #4CAF50; opacity: 0.6"
            animation="property: rotation; to: 0 0 360; dur: 4000; loop: true"
            className="wall-movement-indicator"
          />
        )}
      </a-box>
      
      {/* Artworks positioned relative to wall surface */}
      <a-entity 
        position={`${centerX} ${centerY} ${centerZ}`}
        rotation={`${rotationX} ${rotationY} 0`}
        className="wall-artworks-container"
      >
        {artworks.map((artwork, index) => {
          if (artwork.wall_position_x == null && artwork.wall_position_y == null) {
            console.warn(`⚠️ Artwork ${artwork.vc_id} missing wall position data`);
            return null;
          }
          
          const isSelected = selectedElement?.element_id === artwork.element_id;
          const artworkWidth = (artwork.width || 24) / 12;
          const artworkHeight = (artwork.height || 24) / 12;
          
          // Position artwork relative to wall surface (not world space)
          // Wall coordinates: (0,0) = top-left of wall, artwork position is top-left of artwork
          // Convert to offset from wall center
          const offsetXInches = (artwork.wall_position_x + artwork.width/2) - (wall.width / 2);
          const offsetYInches = (wall.height / 2) - (artwork.wall_position_y + artwork.height/2); // Flip Y axis and center
          
          // Convert inches to feet
          const offsetXFeet = offsetXInches / 12;
          const offsetYFeet = offsetYInches / 12;
          
          // Position artwork on wall surface (relative to wall center)
          const artworkX = offsetXFeet;
          const artworkY = offsetYFeet;
          // Push artwork well in front of the wall and give the frame backing
          // a noticeable gap so it doesn't z-fight at distance.
          const artworkZ = wallDepthFeet/2 + 0.25; // ~3" off the wall surface
          const frameZ = artworkZ - 0.05;          // ~0.6" behind the artwork plane
          
          console.log(`🎨 Artwork ${artwork.vc_id} positioning:`, {
            wallDimensions: `${wall.width}" × ${wall.height}"`,
            wallRotation: `${rotationY}°`,
            wallPosition: `(${artwork.wall_position_x}", ${artwork.wall_position_y}")`,
            offsetInches: `(${offsetXInches.toFixed(1)}", ${offsetYInches.toFixed(1)}")`,
            offsetFeet: `(${offsetXFeet.toFixed(2)}, ${offsetYFeet.toFixed(2)})`,
            relativePosition: `(${artworkX.toFixed(2)}, ${artworkY.toFixed(2)}, ${artworkZ.toFixed(2)})`,
            artworkSize: `${artworkWidth.toFixed(2)} × ${artworkHeight.toFixed(2)}`
          });
          
          return (
            <a-entity key={artwork.element_id} className="artwork-on-wall-container">
              {/* Artwork frame */}
              <a-box
                width={artworkWidth + 0.05}
                height={artworkHeight + 0.05}
                depth="0.01"
                position={`${artworkX} ${artworkY} ${frameZ}`}
                rotation="0 0 0"
                material="color: #000000; roughness: 0.7"
                className="artwork-frame"
              />
              
              {/* Artwork texture plane */}
              <a-plane
                id={`artwork-${artwork.element_id}`}
                width={artworkWidth}
                height={artworkHeight}
                position={`${artworkX} ${artworkY} ${artworkZ}`}
                rotation="0 0 0"
                material={artwork.source ? `shader: flat; src: ${getTextureUrl(artwork.source)}; side: double` : 'color: #374151; opacity: 0.6; transparent: true; side: double'}
                className={`artwork-texture ${isSelected ? 'selected' : ''}`}
                data-artwork-id={artwork.element_id}
                data-vc-id={artwork.vc_id}
                data-wall-id={wall.element_id}
                onClick={() => onSelectElement && onSelectElement(artwork)}
              />
              
              {/* Selection indicator */}
              {isSelected && (
                <a-ring
                  radius-inner={Math.max(artworkWidth, artworkHeight) * 0.6}
                  radius-outer={Math.max(artworkWidth, artworkHeight) * 0.65}
                  position={`${artworkX} ${artworkY} ${artworkZ + 0.05}`}
                  rotation="0 0 0"
                  material="color: #ffff00; opacity: 0.9"
                  animation="property: rotation; to: 0 0 360; dur: 2000; loop: true"
                  className="artwork-selection-ring"
                />
              )}
              
              {/* Artwork ID label (smaller and positioned better) */}
              <a-text
                value={`${artwork.vc_id}`}
                position={`${artworkX} ${artworkY - artworkHeight/2 - 0.15} ${artworkZ + 0.05}`}
                rotation="0 0 0"
                align="center"
                color="#333"
                width="2"
                className="artwork-id-label"
              />
            </a-entity>
          );
        })}
      </a-entity>
      
      {/* Debug: Show wall coordinate system */}
      {/* Remove these after positioning is fixed */}
      <a-sphere
        radius="0.05"
        position={`${centerX} ${centerY} ${centerZ + wallDepthFeet/2 + 0.08}`}
        material="color: red"
        className="wall-center-debug"
      />
      
      {/* Show wall corners for reference */}
      <a-sphere
        radius="0.03"
        position={`${centerX - wallWidthFeet/2} ${centerY + wallHeightFeet/2} ${centerZ + wallDepthFeet/2 + 0.08}`}
        material="color: green"
        className="wall-corner-debug"
      />
      <a-sphere
        radius="0.03"
        position={`${centerX + wallWidthFeet/2} ${centerY + wallHeightFeet/2} ${centerZ + wallDepthFeet/2 + 0.08}`}
        material="color: green"
        className="wall-corner-debug"
      />
      <a-sphere
        radius="0.03"
        position={`${centerX - wallWidthFeet/2} ${centerY - wallHeightFeet/2} ${centerZ + wallDepthFeet/2 + 0.08}`}
        material="color: green"
        className="wall-corner-debug"
      />
      <a-sphere
        radius="0.03"
        position={`${centerX + wallWidthFeet/2} ${centerY - wallHeightFeet/2} ${centerZ + wallDepthFeet/2 + 0.08}`}
        material="color: green"
        className="wall-corner-debug"
      />
    </a-entity>
  );
};

// Note: wallPositionTo3D function is now in utils/api.js with improved coordinate conversion

// Keep the existing positionUnplacedWalls function
export const positionUnplacedWalls = (walls) => {
  const unplacedWalls = walls.filter(wall => 
    !wall.wall_center_x || !wall.wall_center_z
  );
  
  console.log(`🏗️ Auto-positioning ${unplacedWalls.length} unplaced walls`);
  
  return walls.map(wall => {
    if (!wall.wall_center_x || !wall.wall_center_z) {
      const index = unplacedWalls.findIndex(w => w.element_id === wall.element_id);
      const row = Math.floor(index / 3);
      const col = index % 3;
      
      return {
        ...wall,
        wall_center_x: -300 + (col * 180),
        wall_center_y: wall.height / 2,
        wall_center_z: -600 - (row * 120),
        wall_rotation_y: 0,
        needsPositioning: true,
        isStaged: true
      };
    }
    return wall;
  });
};

export default Wall3DBox;